import React, { useState } from "react"

import { css } from "@emotion/react"

import { NIRVANA_THEME } from "@front-shared/config/theme.config"
import type { AvailableTextTypography } from "@front-shared/types/elements.types"
import type { IFaqItem } from "@front-shared/types/objects.types"

import { MarkdownElement } from "./markdown.element"
import { PrimaryText } from "./primaryText.element"

import chevronRight from "@front-shared/assets/images/icons/chevron-right-dark2.svg"

export const FaqElement: React.FunctionComponent<IFaqElementProps> = props => {
    const [expandedIndexes, setExpandedIndexes] = useState<number[]>([])

    const isFaqItemExpanded = (index: number): boolean =>
        expandedIndexes.includes(index)

    const toggleFaqItem = (index: number) => {
        if (isFaqItemExpanded(index)) {
            setExpandedIndexes(current =>
                current.filter(item => item !== index),
            )
        } else {
            setExpandedIndexes(current => [...current, index])
        }
    }

    return (
        <div css={styles.container} className={props.className}>
            <PrimaryText
                typography={props.titleTypography || "h5"}
                color={props.titleColor}
                fontFamily={props.titleFontFamily}
            >
                {props.title || "Frequently Asked Questions"}
            </PrimaryText>

            <div
                className={props.itemsWrapperClassName}
                css={
                    !props.itemsWrapperClassName
                        ? styles.itemsWrapper
                        : undefined
                }
            >
                {props.items.map((item: IFaqItem, index: number) => {
                    const isExpanded =
                        !props.expandable || isFaqItemExpanded(index)

                    return (
                        <div
                            key={index}
                            css={[
                                !props.singleItemClassName
                                    ? styles.singleItem
                                    : undefined,
                            ]}
                            className={`
                                ${props.singleItemClassName}
                                ${props.isColumnLayout ? "flex items-start" : ""}
                            `}
                        >
                            <div
                                className={`
                                    flex items-center
                                    ${props.expandable ? "cursor-pointer" : ""}
                                `}
                                css={[
                                    props.isColumnLayout
                                        ? styles.itemTitleColumnLayout
                                        : undefined,
                                ]}
                                onClick={
                                    props.expandable
                                        ? () => {
                                              toggleFaqItem(index)
                                          }
                                        : undefined
                                }
                            >
                                <PrimaryText
                                    className={`
                                        flex-1
                                        ${props.questionClassName}
                                    `}
                                    typography={
                                        props.questionTypography || "h4"
                                    }
                                    color={props.questionColor}
                                >
                                    {item.question}
                                </PrimaryText>

                                {props.expandable && (
                                    <div
                                        css={[
                                            styles.expandIcon,
                                            isExpanded
                                                ? styles.expandIconExpanded
                                                : undefined,
                                        ]}
                                    >
                                        <img src={chevronRight} alt="Expand" />
                                    </div>
                                )}
                            </div>

                            {isExpanded && (
                                <div
                                    className={
                                        props.isColumnLayout
                                            ? "flex-1"
                                            : undefined
                                    }
                                    css={[styles.answer]}
                                >
                                    {item.isAnswerContentfulMarkdown ? (
                                        <MarkdownElement
                                            className={`contentful-markdown ${props.answerClassName}`}
                                            text={item.answer as string}
                                        />
                                    ) : (
                                        <PrimaryText
                                            className={props.answerClassName}
                                            typography={props.answerTypography}
                                            color={props.answerColor}
                                        >
                                            {item.answer}
                                        </PrimaryText>
                                    )}
                                </div>
                            )}
                        </div>
                    )
                })}
            </div>
        </div>
    )
}

const styles = {
    container: css({
        position: "relative",
        width: "100%",
    }),
    itemsWrapper: css({
        marginTop: 16,
    }),
    singleItem: css({
        marginBottom: 24,
    }),
    itemTitleColumnLayout: css({
        flexBasis: "33%",
        marginRight: 36,
    }),
    expandIcon: css({
        cursor: "pointer",
        position: "relative",
        top: -5,
        marginLeft: 20,
        padding: 5,
    }),
    expandIconExpanded: css({
        transform: "rotate(-90deg)",
    }),
    answer: css({
        [NIRVANA_THEME.mediaQueries.md]: {
            marginTop: 12,
        },
    }),
}

interface IFaqElementProps {
    className?: string
    expandable?: boolean
    isColumnLayout?: boolean
    itemsWrapperClassName?: string
    singleItemClassName?: string
    questionClassName?: string
    answerClassName?: string
    title?: string
    titleTypography?: AvailableTextTypography
    titleColor?: string
    titleFontFamily?: string
    items: IFaqItem[]
    questionTypography?: AvailableTextTypography
    questionColor?: string
    answerTypography?: AvailableTextTypography
    answerColor?: string
}
